import { useState, type ReactElement } from "react";
import pluralize from "pluralize";
import {
  Box,
  Column,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useToast,
} from "@hightouch/htui";
import { flattenFolders, nestFolders } from "src/components/folders/utils";
import {
  useAmpLibraryCreateFolderMutation,
  useAmpLibraryFoldersQuery,
  useAmpLibraryMoveFileMutation,
  type AmpLibraryFileFragment,
} from "src/graphql";
import { FolderPicker } from "src/pages/lifecycle-studio/messages/folder-picker";
import { NO_FOLDER_VALUE } from "src/pages/lifecycle-studio/messages/saved-messages-utils";
import { useFolderPickerState } from "src/pages/lifecycle-studio/messages/use-message-folders";
import {
  MOVE,
  bulkResultToast,
  forEachFile,
} from "src/pages/projects/library/bulk-file-actions";
import { DRAFTS_FOLDER_ID } from "src/pages/projects/library/folder-paths";
import {
  SELECTION_OVERLAY,
  focusPopoverBody,
} from "src/pages/projects/library/use-file-selection";

/** Stands in for the folder a move is going to while that folder is still being created. */
const NEW_FOLDER = "new-folder";

/** Drafts, or a folder (null for the top level). */
type Destination =
  | { drafts: true }
  | { drafts: false; folderId: string | null };

function isAt(file: AmpLibraryFileFragment, destination: Destination) {
  return destination.drafts
    ? file.isDraft
    : !file.isDraft && file.folderId === destination.folderId;
}

function pickerValue(file: AmpLibraryFileFragment) {
  return file.isDraft ? DRAFTS_FOLDER_ID : (file.folderId ?? NO_FOLDER_VALUE);
}

type Props = {
  files: AmpLibraryFileFragment[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** The ids of the files now in the picked folder (moved, or already there), called before the popover closes. */
  onMoved?: (ids: string[]) => void;
  /** The control the popover opens from and is anchored to. */
  children: ReactElement;
  /** "fixed" where the anchor can move, such as a canvas toolbar the canvas pans. */
  strategy?: "fixed";
};

/** Picks Drafts or a folder for the checked files and moves them as soon as one is picked, or makes a new folder to move them into. */
export function MoveFilesPopover({
  files,
  isOpen,
  onOpenChange,
  onMoved,
  children,
  strategy,
}: Props) {
  const picker = useFolderPickerState();
  // The folder a move is going to, while it runs; the picker shows it busy and ignores other clicks.
  const [busyFolderId, setBusyFolderId] = useState<string>();
  const { toast } = useToast();
  const folders = useAmpLibraryFoldersQuery(undefined, { enabled: isOpen });
  const move = useAmpLibraryMoveFileMutation();
  const createFolder = useAmpLibraryCreateFolderMutation();
  const options = flattenFolders(
    nestFolders(
      (folders.data?.ampFileFolders ?? []).map((folder) => ({
        id: folder.id,
        name: folder.name,
        parent_id: folder.parentId,
        type: "amp_files",
        count: 0,
      })),
    ),
  );
  // Files that share a place show it as picked; a mix shows none.
  const [first] = files;
  const current =
    first && files.every((file) => pickerValue(file) === pickerValue(first))
      ? pickerValue(first)
      : undefined;
  // Canvases can't go in Drafts.
  const canDraft = files.every((file) => file.type !== "canvas");

  const title = `Move ${pluralize("file", files.length, true)} to…`;

  const close = () => {
    onOpenChange(false);
    picker.reset();
  };

  const moveTo = async ({
    destination,
    name,
    busyId,
  }: {
    destination: Destination;
    name: string;
    busyId: string;
  }) => {
    // A file already in the destination stays where it is, and counts as moved: only real failures stay checked.
    const toMove = files.filter((file) => !isAt(file, destination));
    const alreadyThere = files
      .filter((file) => isAt(file, destination))
      .map((file) => file.id);
    if (toMove.length === 0) {
      onMoved?.(alreadyThere);
      return close();
    }
    setBusyFolderId(busyId);
    const { done, error } = await forEachFile(toMove, (file) =>
      move.mutateAsync(
        destination.drafts
          ? { id: file.id, drafts: true }
          : { id: file.id, folderId: destination.folderId },
      ),
    );
    setBusyFolderId(undefined);
    toast(
      bulkResultToast({
        verb: MOVE,
        total: toMove.length,
        done: done.length,
        error,
        where: name,
      }),
    );
    // Nothing moved, so the popover stays open to try again.
    if (done.length === 0) return;
    onMoved?.([...alreadyThere, ...done.map((file) => file.id)]);
    close();
  };

  const pick = (id: string | undefined) => {
    if (id === undefined || busyFolderId) return;
    if (id === DRAFTS_FOLDER_ID)
      return void moveTo({
        destination: { drafts: true },
        name: "Drafts",
        busyId: id,
      });
    if (id === NO_FOLDER_VALUE)
      return void moveTo({
        destination: { drafts: false, folderId: null },
        name: "Library",
        busyId: id,
      });
    const folder = options.find((option) => option.id === id);
    if (folder)
      void moveTo({
        destination: { drafts: false, folderId: folder.id },
        name: folder.path.replaceAll("/", " / "),
        busyId: id,
      });
  };

  const create = async () => {
    const name = picker.newFolderName.trim();
    if (!name || busyFolderId) return;
    // Busy from the create through the move, so the Add button spins the whole time.
    setBusyFolderId(NEW_FOLDER);
    try {
      const { createAmpFileFolder } = await createFolder.mutateAsync({
        name,
        parentId: picker.newFolderParentId,
      });
      await moveTo({
        destination: { drafts: false, folderId: createAmpFileFolder.id },
        name: createAmpFileFolder.name,
        busyId: NEW_FOLDER,
      });
      // The folder exists now even if the move failed, so it is picked from the list next time.
      picker.setIsCreating(false);
      picker.setNewFolderName("");
    } catch (error) {
      setBusyFolderId(undefined);
      toast({
        id: "library-create-folder",
        variant: "error",
        title: "Could not create folder",
        message: error instanceof Error ? error.message : "Try again.",
      });
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      placement="bottom-end"
      strategy={strategy}
      onOpen={() => onOpenChange(true)}
      onClose={close}
    >
      <PopoverTrigger>
        {/* The trigger needs an element it can attach to; the control inside keeps its own tooltip and menu. */}
        <Box display="inline-flex">{children}</Box>
      </PopoverTrigger>
      {/* Mounted only while open, so a page of cards carries no hidden portals; the portal lifts it above the card's stacking context. */}
      {isOpen && (
        <Portal>
          <PopoverContent
            width="xs"
            aria-label={title}
            {...{ [SELECTION_OVERLAY]: "" }}
          >
            <PopoverBody p={4}>
              <Column
                ref={focusPopoverBody}
                gap={3}
                aria-busy={busyFolderId !== undefined}
              >
                <Text fontWeight="medium">{title}</Text>
                <FolderPicker
                  folders={options}
                  // Picking a folder moves the files right away, so the picker's selection is the files' current folder.
                  state={{
                    ...picker,
                    selectedId: current,
                    setSelectedId: (value) =>
                      pick(
                        typeof value === "function" ? value(current) : value,
                      ),
                  }}
                  onCreate={() => void create()}
                  isCreatingFolder={busyFolderId === NEW_FOLDER}
                  busyFolderId={busyFolderId}
                  leadingRows={
                    canDraft ? [{ id: DRAFTS_FOLDER_ID, label: "Drafts" }] : []
                  }
                  showNoFolder
                  noFolderLabel="Library"
                  allowNestedCreate
                />
              </Column>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      )}
    </Popover>
  );
}
